import { ComponentProps } from 'preact';
import { DateFieldState } from './getDateFieldInitialReducerState';
import { DateField } from './DateField';
import type { BCP47Locale } from '#UNSAFE_IntlDateTime';
import type { DateGranularity, EditableDateSegment, IsoMonth } from './types';
import {
  getDaysInMonth,
  getMonthsInYear,
  MIN_ISO_YEAR,
  MAX_ISO_YEAR,
  MIN_ISO_DAY,
  MIN_ISO_MONTH
} from '../utils/UNSAFE_calendarDateUtils';
import { numberToString } from './dateReducer';

import { getMonthName } from '#utils/PRIVATE_dayUtils';

type DateFieldType = ComponentProps<typeof DateField>;

type Props = {
  currentState: DateFieldState;
  ariaLabels: Record<EditableDateSegment, string>;
  granularity: DateGranularity;
  locale: BCP47Locale;
  value: DateFieldType['value'];
};

type DerivedSegmentProps = {
  ariaLabels: Record<EditableDateSegment, string>;
  granularity: DateGranularity;
  locale: BCP47Locale;
  value: DateFieldType['value'];
};

type SegmentsType = ReturnType<typeof mergeDerivedStateWithReducerState>['segments'][number];

/**
 * Uses the properties to derive information that can be used to render the DateField and its segments.
 * As much as possible is derived from the properties, and the rest is merged in from the DateFieldState,
 * which is only the segment's isSelected and isComplete and isDateSelected.
 *
 * @param currentState - The current DateFieldState.
 * @param ariaLabels - An object containing aria labels for the date segments.
 * @param granularity - The date granularity.
 * @param locale - The locale.
 * @param value - The new CalendarDate value.
 * @returns A new object with properties needed to render the DateField and its DateSegments.
 */
const mergeDerivedStateWithReducerState = ({
  currentState,
  ariaLabels,
  granularity,
  locale,
  value
}: Props) => {
  const segments = getDerivedDateSegmentRenderProps({
    ariaLabels,
    granularity,
    locale,
    value
  });

  // This is where we are merging in the DateField state with the derived state.
  // We merge in isSelected and isComplete from the currentState. Otherwise we use the derived info.
  const updatedSegments = segments.map((newSegment, index) => {
    const currentSegment = currentState.segments[index];
    return {
      ...newSegment,
      isSelected: currentSegment.isSelected,
      // Since the newSegment's isComplete is true when it has any value,
      // we want to keep the currentSegment.isComplete because it keeps track of what the user is typing.
      isComplete: currentSegment.isComplete
    };
  });

  const isPartialDate =
    value?.year !== undefined || value?.month !== undefined || value?.day !== undefined;

  const isCompleteDate =
    value?.year !== undefined &&
    value?.month !== undefined &&
    (granularity === 'month' || value?.day !== undefined);

  return {
    isCompleteDate,
    isDateSelected: currentState.isDateSelected,
    isPartialDate,
    segments: updatedSegments
  };
};

/**
 * Derives the information to render the date segments based on the provided properties
 * that come from the InputDateMask component.
 *
 * @param props - An object containing the properties used to derive the initial state.
 * @param props.ariaLabels - An object containing aria labels for the date segments.
 * @param props.granularity
 * @param props.locale
 * @param props.value The CalendarDate value
 * @returns The array that holds the information about each segment.
 */
const getDerivedDateSegmentRenderProps = ({
  ariaLabels,
  granularity,
  locale,
  value: currentValue
}: DerivedSegmentProps) => {
  // Derive some data from currentValue that will be used to render the segments
  const yearValue = currentValue?.year;
  const monthValue = currentValue?.month;
  const dayValue = currentValue?.day;
  const monthMax = getMonthsInYear(yearValue ?? '*');
  const dayMax = getDaysInMonth(monthValue ?? '*', yearValue ?? '*');

  const yearValueStr = yearValue ? numberToString(yearValue) : undefined;
  const monthValueStr = monthValue ? numberToString(monthValue) : undefined;
  const dayValueStr = dayValue ? numberToString(dayValue) : undefined;
  const monthAria = monthValue ? getMonthAria(locale, monthValue, yearValue) : undefined;

  // Create state for each editable segment of a date, i.e. year, month, or day.
  const editableSegments = [
    {
      type: 'year' as EditableDateSegment,
      'aria-label': ariaLabels.year,
      'aria-valuemin': MIN_ISO_YEAR,
      'aria-valuemax': MAX_ISO_YEAR,
      'aria-valuenow': yearValue,
      'aria-valuetext': yearValueStr,
      text: yearValueStr
    },
    {
      type: 'month' as EditableDateSegment,
      'aria-label': ariaLabels.month,
      'aria-valuemin': MIN_ISO_MONTH,
      'aria-valuemax': monthMax,
      'aria-valuenow': monthValue,
      'aria-valuetext': monthAria,
      text: monthValueStr
    },
    ...(granularity === 'day'
      ? [
          {
            type: 'day' as EditableDateSegment,
            'aria-label': ariaLabels.day,
            'aria-valuemin': MIN_ISO_DAY,
            'aria-valuemax': dayMax,
            'aria-valuenow': dayValue,
            'aria-valuetext': dayValueStr,
            text: dayValueStr
          }
        ]
      : [])
  ];
  return editableSegments;
};

// Determine the aria-valuetext for the given month, which includes the month name.
const getMonthAria = (locale: BCP47Locale, month: IsoMonth, year: number | undefined) => {
  const monthName = getMonthName(locale, month, year ?? '*', 'long');
  return `${month} - ${monthName}`;
};

export { mergeDerivedStateWithReducerState };
export type { SegmentsType };
